/**
 * Utility service to handle local file uploads, validation, and optimization.
 */
export const uploadService = {
  // Limits
  MAX_IMAGE_SIZE_MB: 10,
  MAX_VIDEO_SIZE_MB: 30,
  TARGET_IMAGE_DIMENSION: 1920, // Max width/height after compression
  IMAGE_QUALITY: 0.82,

  // Explicit web-friendly formats
  SUPPORTED_IMAGE_TYPES: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
  SUPPORTED_VIDEO_TYPES: ['video/mp4', 'video/webm'],

  /**
   * Compresses an image using Canvas before converting to Base64
   */
  async compressImage(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Calculate new dimensions
          if (width > height) {
            if (width > this.TARGET_IMAGE_DIMENSION) {
              height *= this.TARGET_IMAGE_DIMENSION / width;
              width = this.TARGET_IMAGE_DIMENSION;
            }
          } else {
            if (height > this.TARGET_IMAGE_DIMENSION) {
              width *= this.TARGET_IMAGE_DIMENSION / height;
              height = this.TARGET_IMAGE_DIMENSION;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (!ctx) return reject(new Error('Canvas context failed'));

          // Draw and compress
          ctx.drawImage(img, 0, 0, width, height);
          const compressedBase64 = canvas.toDataURL('image/jpeg', this.IMAGE_QUALITY);
          resolve(compressedBase64);
        };
        img.onerror = () => reject(new Error('Image loading failed during compression'));
      };
      reader.onerror = (error) => reject(error);
    });
  },

  /**
   * Converts a File object to a Base64 string with optimization for images
   */
  async fileToBase64(file: File): Promise<string> {
    const sizeInMB = file.size / (1024 * 1024);
    const isVideo = file.type.startsWith('video/');
    const isImage = file.type.startsWith('image/');

    // 1. Check Size
    const limit = isVideo ? this.MAX_VIDEO_SIZE_MB : this.MAX_IMAGE_SIZE_MB;
    if (sizeInMB > limit) {
      throw new Error(`File too large. Max ${limit}MB allowed.`);
    }

    // 2. Check Format
    const allSupported = [...this.SUPPORTED_IMAGE_TYPES, ...this.SUPPORTED_VIDEO_TYPES];
    if (!allSupported.includes(file.type)) {
      throw new Error(`Unsupported file type: "${file.type}". Please use JPG, PNG, or MP4.`);
    }

    // 3. Process
    if (isImage) {
      return await this.compressImage(file);
    }

    // For videos, just do raw conversion (client-side video compression is too heavy for simple canvas)
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  },

  /**
   * Triggers a hidden file input and returns the optimized Base64
   */
  async selectAndUpload(): Promise<string> {
    return new Promise((resolve, reject) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.jpg,.jpeg,.png,.webp,.mp4,.webm';
      
      input.onchange = async (e: Event) => {
        const target = e.target as HTMLInputElement;
        const file = target.files?.[0];
        
        if (file) {
          try {
            const base64 = await this.fileToBase64(file);
            resolve(base64);
          } catch (err) {
            reject(err);
          }
        } else {
          reject(new Error('No file selected'));
        }
      };

      input.click();
    });
  }
};